import { useState, type ReactNode } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EditDrawer } from "./EditDrawer";

/**
 * An edit drawer whose Save persists the change and then closes.
 *
 * The CMS rule (UI-FIX): editing happens in a drawer, Save lives in the
 * drawer, and the outcome is a toast - never a page-level "Unsaved changes"
 * bar. `onSave` does the write (usually `workspace.commit(...)`, which toasts)
 * and resolves true when it worked: the drawer then slides out. On false it
 * stays open with the user's input intact so they can retry.
 *
 * Mount it only while it is open (`{open && <CommitDrawer .../>}`) and keep the
 * edit state in the component that renders it: the state is then fresh each
 * time and a Cancel throws it away.
 */
export default function CommitDrawer({
  title,
  subtitle,
  onClose,
  onSave,
  saveLabel,
  saveDisabled = false,
  width,
  children,
  "data-testid": testId,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  onSave: () => Promise<boolean>;
  saveLabel?: string;
  saveDisabled?: boolean;
  width?: number;
  children: ReactNode;
  "data-testid"?: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <EditDrawer
      open
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width={width}
      busy={busy}
      data-testid={testId}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={busy || saveDisabled}
            onClick={async () => {
              setBusy(true);
              let ok = false;
              try {
                ok = await onSave();
              } finally {
                setBusy(false);
              }
              if (ok) dismiss();
            }}
            data-testid="drawer-save"
          >
            {busy ? t("states.saving") : (saveLabel ?? t("buttons.save"))}
          </Button>
        </>
      )}
    >
      {children}
    </EditDrawer>
  );
}
